import apiClient from './api-client';
import fcmService from './fcm-service';
import { Capacitor } from '@capacitor/core';
import { toast } from 'react-toastify';
import logger from '../shared/utils/logger';

export interface Notification {  
  notificationId: number;
  employeeId: number;
  type: string;
  title: string;
  message: string;
  data?: Record<string, unknown>;
  isRead?: boolean;
  createdAt: string;
}

export interface NotificationPaginationMeta {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface NotificationResponse {
  data: Notification[];
  meta: NotificationPaginationMeta;
  unread_count: number;
}

export interface NotificationCount {
  total: number;
  unread: number;
}

export interface NotificationFilters {
  page?: number;
  limit?: number;
  is_read?: boolean;
  type?: string;
  sortField?: string;
  sortDirection?: 'ASC' | 'DESC';
}

type NotificationCallback = (notification: Notification) => void;

/**
 * Notification service for handling notification API operations
 */
class NotificationService {
  private notificationEndpoint = '/notifications';
  private subscribers: NotificationCallback[] = [];
  private pollingInterval: number | null = null;
  private pollingDelay: number = 30000; // 30 seconds
  private userId: string | null = null;
  private initialized: boolean = false;
  private lastNotificationId: number | null = null;
  private useFcm: boolean = true;

  /**
   * Initialize the notification service
   * @param userId User ID
   * @param useFcm Whether to use FCM (default: true)
   * @returns Promise<void>
   */
  public async init(userId: string, useFcm: boolean = true): Promise<void> {
    if (this.initialized && this.userId === userId) {
      return;
    }
    
    this.userId = userId;
    this.useFcm = useFcm;

    try {
      // Initialize FCM if enabled and platform supports it
      if (this.useFcm) {
        try {
          // Set up the token registration callback first to break circular dependency
          fcmService.setTokenRegistrationCallback(this.registerDevice.bind(this));
          
          // Check if platform supports FCM
          const fcmSupported = await fcmService.isSupported();
          
          if (fcmSupported) {
            await fcmService.initialize(userId);

            fcmService.registerNotificationCallback(this.handleNewNotification.bind(this));

            // On web, push only delivers once the user has granted permission and a
            // token was obtained (see fcmService.enableWebPush). Until then, poll so
            // notifications still arrive. Native tokens arrive asynchronously via a
            // listener, so don't poll there.
            if (!Capacitor.isNativePlatform() && !fcmService.getFCMToken()) {
              this.startPolling();
            }
          } else {
            this.useFcm = false;
            this.startPolling();
          }
        } catch (error: unknown) {
          logger.warn('FCM initialization failed, falling back to polling:', error);
          this.useFcm = false;
          this.startPolling();
        }
      } else {
        this.startPolling();
      }
      
      this.initialized = true;
    } catch (error: unknown) {
      logger.error('Error initializing notification service:', error);
      throw error;
    }
  }

  /**
   * Start polling for notifications
   */
  private startPolling(): void {
    // Clear any existing interval first
    this.stopPolling();
    
    // Poll every 30 seconds
    this.pollingInterval = window.setInterval(() => {
      this.checkForNewNotifications();
    }, this.pollingDelay);
    
    // Initial check
    this.checkForNewNotifications();
  }

  /**
   * Stop polling for notifications
   */
  private stopPolling(): void {
    if (this.pollingInterval !== null) {
      window.clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  /**
   * Check for new notifications
   */
  private async checkForNewNotifications(): Promise<void> {
    if (!this.userId) return;

    try {
      const response = await this.getNotificationsPaginated({
        page: 1,
        limit: 5,
        sortField: 'created_at',
        sortDirection: 'DESC'
      });

      // Process new notifications if any
      if (response?.data?.length > 0) {
        const latestNotification = response.data[0];
        
        // If we have a new notification that we haven't seen before
        if (this.lastNotificationId !== null && 
            latestNotification.notificationId > this.lastNotificationId) {
          
          // Find all new notifications
          const newNotifications = response.data.filter(n => 
            n.notificationId > (this.lastNotificationId || 0)
          );
          
          // Notify subscribers about new notifications
          newNotifications.forEach(notification => {
            this.handleNewNotification(notification);
          });
        }
        
        // Update last notification ID
          if (latestNotification.notificationId > (this.lastNotificationId || 0)) {
          this.lastNotificationId = latestNotification.notificationId;
        }
      }
    } catch (error: unknown) {
      logger.error('Error checking for new notifications:', error);
    }
  }

  /**
   * Handle new notification
   * @param notification Notification object
   */
  private handleNewNotification(notification: Notification): void {
    // Show toast notification
    toast.info(`${notification.title}: ${notification.message}`, {
      autoClose: 5000,
      closeButton: true,
      position: 'top-right',
    });
    
    // Notify subscribers
    this.subscribers.forEach(callback => {
      try {
        callback(notification);
      } catch (error: unknown) {
        logger.error('Error in notification subscriber callback:', error);
      }
    });
  }

  /**
   * Subscribe to new notifications
   * @param callback Callback function
   * @returns Unsubscribe function
   */
  public subscribe(callback: NotificationCallback): () => void {
    if (this.subscribers.length >= 100) {
      logger.warn('Max notification subscribers reached, removing oldest');
      this.subscribers.shift();
    }
    this.subscribers.push(callback);

    // Return unsubscribe function
    return () => {
      this.subscribers = this.subscribers.filter(cb => cb !== callback);
    };
  }

  /**
   * Disconnect and cleanup resources
   */
  public disconnect(): void {
    this.stopPolling();
    if (this.useFcm) {
      fcmService.cleanup();
    }
    this.subscribers = [];
    this.initialized = false;
    this.userId = null;
    this.lastNotificationId = null;
  }

  /**
   * Get all notifications with pagination
   * @param filters Optional filters
   * @returns Promise with the notification response
   */
  public async getNotificationsPaginated(filters: NotificationFilters = {}): Promise<NotificationResponse> {
    try {
      const params = new URLSearchParams();
      params.append('page', filters.page?.toString() || '1');
      params.append('limit', filters.limit?.toString() || '20');
      
      if (filters.is_read !== undefined) {
        params.append('is_read', filters.is_read.toString());
      }
      
      if (filters.type) {
        params.append('type', filters.type);
      }
      
      if (filters.sortField) {
        params.append('sortField', filters.sortField);
        params.append('sortDirection', filters.sortDirection || 'DESC');
      }

      return await apiClient.get<NotificationResponse>(`${this.notificationEndpoint}?${params.toString()}`);
    } catch (error: unknown) {
      logger.error('Error fetching paginated notifications:', error);
      throw error;
    }
  }

  /**
   * Get all notifications (for backward compatibility)
   * @returns Promise with the notifications array
   */
  public async getNotifications(): Promise<Notification[]> {
    try {
      const response = await this.getNotificationsPaginated();
      return response.data;
    } catch (error: unknown) {
      logger.error('Error fetching notifications:', error);
      throw error;
    }
  }

  /**
   * Get notification count
   * @returns Promise with the notification count
   */
  public async getNotificationCount(): Promise<NotificationCount> {
    try {
      return await apiClient.get<NotificationCount>(`${this.notificationEndpoint}/count`);
    } catch (error: unknown) {
      logger.error('Error fetching notification count:', error);
      throw error;
    }
  }

  /**
   * Mark a notification as read
   * @param id The notification ID
   * @returns Promise with void
   */
  public async markAsRead(id: number): Promise<void> {
    try {
      return await apiClient.put<void>(`${this.notificationEndpoint}/${id}/read`, {});
    } catch (error: unknown) {
      logger.error(`Error marking notification ${id} as read:`, error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read
   * @returns Promise with void
   */
  public async markAllAsRead(): Promise<void> {
    try {
      return await apiClient.put<void>(`${this.notificationEndpoint}/read-all`, {});
    } catch (error: unknown) {
      logger.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  /**
   * Clear all notifications
   * @returns Promise with void
   */
  public async clearAll(): Promise<void> {
    try {
      return await apiClient.delete<void>(`${this.notificationEndpoint}/clear`);
    } catch (error: unknown) {
      logger.error('Error clearing all notifications:', error);
      throw error;
    }
  }

  /**
   * Register a device token for FCM notifications
   * @param token The FCM token
   * @param deviceType The device type (web, android, ios)
   * @returns Promise with void
   */
  public async registerDevice(token: string, deviceType: string = 'web'): Promise<void> {
    try {
      const result = await apiClient.post<void>(`${this.notificationEndpoint}/register-device`, {
        token,
        device_type: deviceType
      });

      // Push is now live for this device, so the polling fallback must stop.
      // Otherwise every notification arrives twice — once via FCM onMessage and
      // once when the poller picks up the same row — which is exactly what
      // happens after enableWebPush() grants permission mid-session.
      this.stopPolling();

      return result;
    } catch (error: unknown) {
      logger.error('Error registering device token:', error);
      throw error;
    }
  }

  /**
   * Unregister a device token so the backend stops targeting this device.
   *
   * Called on logout. skipErrorToast because a failure here is not the user's
   * problem — the server purges the row on logout anyway, and a red toast on the
   * way to the login screen is pure noise.
   *
   * @param token The FCM token
   * @returns Promise with void
   */
  public async unregisterDevice(token: string): Promise<void> {
    try {
      return await apiClient.delete<void>(`${this.notificationEndpoint}/unregister-device`, {
        data: { token },
        skipErrorToast: true
      });
    } catch (error: unknown) {
      logger.error('Error unregistering device token:', error);
      throw error;
    }
  }

  /**
   * Send a test notification (admin only)
   * @param title The notification title
   * @param message The notification message
   * @param type The notification type
   * @param data Additional data
   * @returns Promise with the created notification
   */
  public async sendTestNotification(
    title: string, 
    message: string, 
    type: string = 'system', 
    data: Record<string, unknown> = {}
  ): Promise<Notification> {
    try {
      return await apiClient.post<Notification>(`${this.notificationEndpoint}/test`, {
        title,
        message,
        type,
        data: JSON.stringify(data)
      });
    } catch (error: unknown) {
      logger.error('Error sending test notification:', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const notificationService = new NotificationService();
export default notificationService; 